import { UploadedFile } from "components/InputMultipleFiles";
import { useFormik } from "formik";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileService } from "services/fileService";
import { checkChange } from "utils/checkChange";
import * as Yup from "yup";

export interface ExhibitorProps {
  name: string;
  description: string;
  websiteUrl: string;
  boothNumber: string;
  sponsorTier: string;
  isSponsor: boolean;
  contactEmail: string;
  contactPhone: string;
  displayPosition?: number;
  logo?: {
    signedFileUrl?: string;
  };
}

const initInputsValues: ExhibitorProps = {
  name: "",
  description: "",
  websiteUrl: "",
  boothNumber: "",
  sponsorTier: "",
  isSponsor: false,
  contactEmail: "",
  contactPhone: "",
  displayPosition: undefined,
};

const initTouchedInputsValues = {
  name: false,
  description: false,
  websiteUrl: false,
  boothNumber: false,
  sponsorTier: false,
  isSponsor: false,
  contactEmail: false,
  contactPhone: false,
  displayPosition: false,
  logo: false,
};

export const useExhibitorForm = (
  onSuccess?: () => void,
  editingExhibitor?: any,
  providedEventId?: string
) => {
  const { id: paramEventId, eventId: paramEventIdAlt } = useParams<{
    id: string;
    eventId: string;
  }>();
  const eventId = providedEventId || paramEventId || paramEventIdAlt;
  const { setToast } = useToast();
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();

  const [initialInputsValues, setInitialInputsValues] = useState(() => {
    if (editingExhibitor) {
      const values = {
        name: editingExhibitor.name || "",
        description: editingExhibitor.description || "",
        websiteUrl: editingExhibitor.websiteUrl || "",
        boothNumber: editingExhibitor.boothNumber || "",
        sponsorTier: editingExhibitor.sponsorTier || "",
        isSponsor: editingExhibitor.isSponsor || false,
        contactEmail: editingExhibitor.contactEmail || "",
        contactPhone: editingExhibitor.contactPhone || "",
        displayPosition: editingExhibitor.displayPosition || undefined,
        logo: editingExhibitor.logo || undefined,
      };
      return values;
    }
    return initInputsValues;
  });
  const [changeFooter, setChangeFooter] = useState<string[]>([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [logoIsPNG, setLogoIsPNG] = useState<boolean>(true);

  // Multiple files state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  const {
    handleSubmit,
    handleChange,
    resetForm,
    setValues: setInputsValues,
    values: inputsValues,
    touched,
    errors,
    setFieldError,
    setFieldTouched,
  } = useFormik({
    initialValues: initialInputsValues,
    initialTouched: initTouchedInputsValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Nome é obrigatório"),
      description: Yup.string().optional(),
      websiteUrl: Yup.string().url("URL inválida").optional(),
      boothNumber: Yup.string().optional(),
      sponsorTier: Yup.string().optional(),
      isSponsor: Yup.boolean().optional(),
      contactEmail: Yup.string().email("E-mail inválido").optional(),
      contactPhone: Yup.string().optional(),
      displayPosition: Yup.number()
        .optional()
        .nullable()
        .min(1, "Ordem deve ser maior que 0")
        .integer("Ordem deve ser um número inteiro"),
      logo: Yup.mixed().optional(),
    }),
    onSubmit: (_) => {
      sendData();
    },
  });

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  useEffect(() => {
    const changed = checkChange(inputsValues, initialInputsValues, true);
    setChangeFooter(changed);
  }, [inputsValues, initialInputsValues]);

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues]);

  useEffect(() => {
    if (editingExhibitor) {
      const newValues = {
        name: editingExhibitor.name || "",
        description: editingExhibitor.description || "",
        websiteUrl: editingExhibitor.websiteUrl || "",
        boothNumber: editingExhibitor.boothNumber || "",
        sponsorTier: editingExhibitor.sponsorTier || "",
        isSponsor: editingExhibitor.isSponsor || false,
        contactEmail: editingExhibitor.contactEmail || "",
        contactPhone: editingExhibitor.contactPhone || "",
        displayPosition: editingExhibitor.displayPosition || undefined,
        logo: editingExhibitor.logo || undefined,
      };

      setInitialInputsValues(newValues);

      // Set logo preview if editing exhibitor has logo
      if (editingExhibitor.logo?.signedFileUrl) {
        setImagePath(editingExhibitor.logo.signedFileUrl);
      }

      // Clear previous files first
      setUploadedFiles([]);
      setFilesToUpload([]);

      // Load existing files if editing exhibitor has files
      if (editingExhibitor.files && Array.isArray(editingExhibitor.files)) {
        const existingFiles: UploadedFile[] = editingExhibitor.files.map(
          (file: any, index: number) => {
            const filename = file.filename || `Arquivo ${index + 1}`;

            // Detectar imagem baseado no mimeType que vem como extensão (jpg, png, etc)
            const imageExtensions = [
              "jpg",
              "jpeg",
              "png",
              "gif",
              "bmp",
              "webp",
              "svg",
            ];
            const isImage =
              file.mimeType &&
              imageExtensions.includes(file.mimeType.toLowerCase());

            return {
              id: file.fileId,
              file: null, // Existing files don't have File object
              name: filename,
              size: "Arquivo existente",
              previewUrl:
                isImage && file.signedFileUrl ? file.signedFileUrl : undefined,
            };
          }
        );

        setUploadedFiles(existingFiles);
      }
    } else {
      // Reset to initial values when not editing
      setInitialInputsValues(initInputsValues);
      setImagePath(null);
      setUploadedFiles([]);
      setFilesToUpload([]);
    }
  }, [editingExhibitor]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        if (file.previewUrl && file.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, []);

  /* EVENT HANDLERS */
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
  };

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    handleChange(event);
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setInputsValues((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const customHandleLogoChange = async (file: File) => {
    setLogoFile(file);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    if (file.type === "image/png") {
      setLogoIsPNG(true);
      const objectUrl = URL.createObjectURL(file); // Cria URL para preview
      setImagePath(objectUrl);
      customHandleLogoChange(file);
      handleChange({
        target: {
          name: "logo.signedFileUrl",
          value: file.name,
        },
      } as any);
    } else {
      setLogoIsPNG(false);
      setImagePath(null);
    }
  };

  // Multiple files handlers
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAddFiles = (files: File[]) => {
    const newUploadedFiles: UploadedFile[] = files.map((file) => {
      const isImage = file.type.startsWith("image/");
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined;

      return {
        id: `${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: formatFileSize(file.size),
        previewUrl,
      };
    });

    setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
    setFilesToUpload((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = async (fileId: string) => {
    const fileToRemove = uploadedFiles.find((f) => f.id === fileId);
    if (!fileToRemove) return;

    // Clean up object URL to prevent memory leaks
    if (
      fileToRemove.previewUrl &&
      fileToRemove.previewUrl.startsWith("blob:")
    ) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }

    // If it's a new file (has file property), just remove from state
    if (fileToRemove.file) {
      setFilesToUpload((prev) => prev.filter((f) => f !== fileToRemove.file));
      setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
      return;
    }

    // If it's an existing file (no file property), call API to delete from backend
    try {
      const response = await connectGraphQLApi({
        query: `
          mutation BackofficeDeleteFile($fileId: ID!) {
            backofficeDeleteFile(fileId: $fileId)
          }
        `,
        variables: { fileId },
        errorMessage: "Falha ao deletar arquivo",
        useNewApi: true,
      });

      if (response.data.backofficeDeleteFile) {
        setToast({
          type: "success",
          message: "Arquivo deletado com sucesso",
          isActive: true,
          timeToRemoveToast: 3000,
        });

        // Remove from UI only after successful deletion
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
      } else {
        setToast({
          type: "error",
          message: "Falha ao deletar arquivo",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      setToast({
        type: "error",
        message: "Erro ao deletar arquivo. Tente novamente.",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    }
  };

  const resetFormData = () => {
    resetForm();
    setLogoFile(null);
    setImagePath(null);
    setLogoIsPNG(true);
    setChangeFooter([]);
    setSubmitButton(false);
    setIsLoading(false);
    setHasError(false);
    setInitialInputsValues(initInputsValues);

    // Clean up object URLs before clearing files
    uploadedFiles.forEach((file) => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });

    setUploadedFiles([]);
    setFilesToUpload([]);
  };

  /* API CALLS */
  const sendData = async () => {
    setSubmitButton(true);
    setIsLoading(true);
    cancelRequest();

    const isEditing = !!editingExhibitor;

    const query = isEditing
      ? `
      mutation BackofficeUpdateExhibitor($exhibitorId: ID!, $input: ExhibitorUpdateInput!) {
        backofficeUpdateExhibitor(exhibitorId: $exhibitorId, input: $input) {
          exhibitorId
          eventId
          name
          description
          websiteUrl
          boothNumber
          sponsorTier
          isSponsor
          contactEmail
          contactPhone
          insertDate
          updateDate
        }
      }
    `
      : `
      mutation BackofficeCreateExhibitor($input: ExhibitorInput!) {
        backofficeCreateExhibitor(input: $input) {
          exhibitorId
          eventId
          name
          description
          websiteUrl
          boothNumber
          sponsorTier
          isSponsor
          contactEmail
          contactPhone
          insertDate
          updateDate
        }
      }
    `;

    // Build input object, only including non-empty values
    const input: any = {
      name: inputsValues.name.trim(),
      isSponsor: inputsValues.isSponsor || false,
    };

    // For creation, add eventId
    if (!isEditing) {
      input.eventId = eventId;
    }

    // Only add optional fields if they have values
    if (inputsValues.description?.trim()) {
      input.description = inputsValues.description.trim();
    }
    if (inputsValues.websiteUrl?.trim()) {
      input.websiteUrl = inputsValues.websiteUrl.trim();
    }
    if (inputsValues.boothNumber?.trim()) {
      input.boothNumber = inputsValues.boothNumber.trim();
    }
    if (inputsValues.sponsorTier?.trim()) {
      input.sponsorTier = inputsValues.sponsorTier.trim();
    }
    if (inputsValues.contactEmail?.trim()) {
      input.contactEmail = inputsValues.contactEmail.trim();
    }
    if (inputsValues.contactPhone?.trim()) {
      input.contactPhone = inputsValues.contactPhone.trim();
    }
    if (
      inputsValues.displayPosition !== undefined &&
      inputsValues.displayPosition !== null
    ) {
      input.displayPosition = parseInt(inputsValues.displayPosition.toString());
    }

    const variables = isEditing
      ? {
          exhibitorId: editingExhibitor.exhibitorId,
          input,
        }
      : {
          input,
        };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: isEditing
          ? "Falha ao atualizar expositor"
          : "Falha ao criar expositor",
        useNewApi: true,
      });

      // Upload logo if provided
      if (logoFile) {
        const exhibitorId = isEditing
          ? response.data.backofficeUpdateExhibitor.exhibitorId
          : response.data.backofficeCreateExhibitor.exhibitorId;

        await FileService.uploadFile({
          file: logoFile,
          refCollectionId: exhibitorId,
          refCollectionName: "exhibitors",
          refCollectionField: "logoId",
          refCollectionFieldType: "string",
        });
      }

      // Upload multiple files if provided
      if (filesToUpload.length > 0) {
        const exhibitorId = isEditing
          ? response.data.backofficeUpdateExhibitor.exhibitorId
          : response.data.backofficeCreateExhibitor.exhibitorId;

        for (const file of filesToUpload) {
          await FileService.uploadFile({
            file,
            refCollectionId: exhibitorId,
            refCollectionName: "exhibitors",
            refCollectionField: "fileIds",
            refCollectionFieldType: "array",
          });
        }
      }

      setToast({
        type: "success",
        message: isEditing
          ? "Expositor atualizado com sucesso."
          : "Expositor criado com sucesso.",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      resetFormData();
      onSuccess && onSuccess();
    } catch (error: any) {
      console.error("Error creating exhibitor:", error);

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const errorMessage =
          error.graphQLErrors[0].message ||
          (isEditing
            ? "Erro ao atualizar expositor"
            : "Erro ao criar expositor");
        setToast({
          type: "error",
          message: errorMessage,
          isActive: true,
          timeToRemoveToast: 5000,
        });
      } else if (error.message) {
        setToast({
          type: "error",
          message:
            error.message ||
            (isEditing
              ? "Falha ao atualizar expositor. Tente novamente."
              : "Falha ao criar expositor. Tente novamente."),
          isActive: true,
          timeToRemoveToast: 5000,
        });
      } else {
        setToast({
          type: "error",
          message: isEditing
            ? "Erro desconhecido ao atualizar expositor."
            : "Erro desconhecido ao criar expositor.",
          isActive: true,
          timeToRemoveToast: 5000,
        });
      }
    } finally {
      setSubmitButton(false);
      setIsLoading(false);
    }
  };

  return {
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    inputsValues,
    touched,
    errors,
    changeFooter,
    submitButton,
    handleSubmit,
    hasError,
    isLoading,
    customHandleLogoChange,
    handleImageChange,
    imagePath,
    logoIsPNG,
    resetFormData,
    isEditing: !!editingExhibitor,
    // Multiple files
    uploadedFiles,
    handleAddFiles,
    handleRemoveFile,
  };
};
